import type { AdminUserListFilters } from '../types/adminUsers'
import type { TenantIntegrationFilters } from '../types/tenantIntegrations'
import type { ProductServiceCategoryListFilters, ProductServiceListFilters, ProductType } from '../types/productsServices'
import type { InvoicesAnalysisFilters } from '../types/invoicesAnalysis'
import type { InvoiceListFilters } from '../types/invoices'
import type {
  MarginAnalysisFilters,
  MarginComparisonTarget,
  MarginSummaryGroupBy,
  MarginTrendGranularity,
  MarginsKpiFilters,
  SalesAnalysisFilters,
  SalesKpiFilters,
} from '../types/salesAnalysis'
import type { CostsAnalysisFilters } from '../types/costsAnalysis'

export const queryKeys = {
  invoices: {
    all: (schemaName: string) => ['invoices', schemaName] as const,
    list: (schemaName: string, filters: InvoiceListFilters) =>
      ['invoices', schemaName, 'list', filters] as const,
    listInfinite: (schemaName: string, filters: Omit<InvoiceListFilters, 'page' | 'pageSize'>) =>
      ['invoices', schemaName, 'listInfinite', filters] as const,
    analysis: (schemaName: string, filters: InvoicesAnalysisFilters) =>
      ['invoices', schemaName, 'analysis', filters] as const,
    lines: (schemaName: string, invoiceId: number, sortBy: string | null, sortDirection: string | null) =>
      ['invoices', schemaName, 'lines', invoiceId, sortBy, sortDirection] as const,
  },
  sales: {
    all: (schemaName: string) => ['sales', schemaName] as const,
    kpi: (schemaName: string, filters: SalesKpiFilters) =>
      ['sales', schemaName, 'kpi', filters] as const,
    analysis: (schemaName: string, filters: SalesAnalysisFilters) =>
      ['sales', schemaName, 'analysis', filters] as const,
  },
  margins: {
    all: (schemaName: string) => ['margins', schemaName] as const,
    kpi: (schemaName: string, filters: MarginsKpiFilters) =>
      ['margins', schemaName, 'kpi', filters] as const,
    analysis: (
      schemaName: string,
      filters: MarginAnalysisFilters,
      groupBy: MarginSummaryGroupBy,
      granularity: MarginTrendGranularity,
      effectiveDate: { start: string; end: string } | null | undefined,
    ) => ['margins', schemaName, 'analysis', filters, groupBy, granularity, effectiveDate ?? null] as const,
    comparison: (
      schemaName: string,
      filters: MarginAnalysisFilters,
      granularity: MarginTrendGranularity,
      targets: MarginComparisonTarget[],
    ) => ['margins', schemaName, 'comparison', filters, granularity, targets] as const,
    filterComparison: (
      schemaName: string,
      filters: MarginAnalysisFilters,
      granularity: MarginTrendGranularity,
    ) => ['margins', schemaName, 'filterComparison', filters, granularity] as const,
  },
  overview: {
    data: (schemaName: string, startDate: string, endDate: string, structureIds?: number[] | null) =>
      ['overview', schemaName, startDate, endDate, structureIds ?? null] as const,
  },
  products: {
    all: (schemaName: string) => ['products', schemaName] as const,
    list: (schemaName: string, filters: ProductServiceListFilters, scope?: ProductType[]) =>
      ['products', schemaName, 'list', filters, scope ?? null] as const,
    categories: (schemaName: string) => ['products', schemaName, 'categories'] as const,
    components: (schemaName: string, productId: number) =>
      ['products', schemaName, 'components', productId] as const,
    rawMaterials: (schemaName: string) => ['products', schemaName, 'rawMaterials'] as const,
    sellable: (schemaName: string) => ['products', schemaName, 'sellable'] as const,
    expensesAll: (schemaName: string) => ['products', schemaName, 'expenses-all'] as const,
    listInfinite: (
      schemaName: string,
      filters: Omit<ProductServiceListFilters, 'page' | 'pageSize'>,
      scope?: ProductType[],
    ) => ['products', schemaName, 'infinite', filters, scope ?? null] as const,
  },
  structures: {
    list: (schemaName: string) => ['structures', schemaName] as const,
  },
  entities: {
    all: (schemaName: string) => ['entities', schemaName, 'all'] as const,
  },
  vatRates: {
    list: (schemaName: string) => ['vatRates', schemaName] as const,
  },
  adminUsers: {
    all: (schemaName: string) => ['adminUsers', schemaName] as const,
    list: (schemaName: string, filters: AdminUserListFilters) =>
      ['adminUsers', schemaName, 'list', filters] as const,
  },
  lastUpdate: {
    data: (schemaName: string) => ['lastUpdate', schemaName] as const,
  },
  menuCategories: {
    all: (schemaName: string) => ['menuCategories', schemaName] as const,
    list: (schemaName: string, filters: ProductServiceCategoryListFilters) =>
      ['menuCategories', schemaName, 'list', filters] as const,
    listInfinite: (schemaName: string, filters: Omit<ProductServiceCategoryListFilters, 'page' | 'pageSize'>) =>
      ['menuCategories', schemaName, 'listInfinite', filters] as const,
  },
  adminIntegrations: {
    all: () => ['adminIntegrations'] as const,
    list: (filters: TenantIntegrationFilters) => ['adminIntegrations', 'list', filters] as const,
    tenants: () => ['adminIntegrations', 'tenants'] as const,
    publicKey: () => ['adminIntegrations', 'publicKey'] as const,
  },
  permissions: {
    user: (userId: string) => ['permissions', 'user', userId] as const,
  },
  costsAnalysis: {
    all: (schemaName: string) => ['costsAnalysis', schemaName] as const,
    summary: (schemaName: string, filters: CostsAnalysisFilters) =>
      ['costsAnalysis', schemaName, 'summary', filters] as const,
    details: (schemaName: string, filters: CostsAnalysisFilters) =>
      ['costsAnalysis', schemaName, 'details', filters] as const,
  },
}
